import _, { stubTrue } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Collapse, Dropdown, DropdownItem } from 'reactstrap';

import { PanelMainGroup, PanelSection } from '../SidePanel';
import IconButton from '../basics/IconButton';
import { useTilesetContext } from '../../contexts/TilesetContext';
import { useQueryWithMsg } from '../../utils/hooks';
import { Message } from '../templates/template-components';
import { EllipsisText, StyledMenu } from '../basics/DropdownButton';
import { useIsMobile } from '../../utils/responsive';
import ChevronIcon from '../icons/ChevronIcon';
import { UncontrolledWhiteTooltip, WhiteTooltip } from '../basics/WhiteTooltip';
import { getHighlightColors } from '../../utils/tilesets';
import { TilesetsQuery } from '../../queries';
import { MapContext } from '../../contexts/MapContext';
import { TILESET_MAX_ZOOM } from '../../constants/tilesets';
import WarningIcon from '../icons/WarningIcon';
import DiscreteKey from './DiscreteKey';
import ContinuousKey from './ContinuousKey';
import { Tileset, TILESET_TYPE_CROP } from '../../types/tileset';

const LULC_ZOOM_WARNING =
  'LULC data tiles are accurate down to 30 meters. If you zoom in, you may see inaccuracies in labeling';

const Disclaimer = () => {
  const isMobile = useIsMobile();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const toggleDisclaimerOpen = () => setDisclaimerOpen(!disclaimerOpen);
  const disclaimerContent = (
    <div className="font-tiny">
      <span className="text-muted font-tiny">Crop suitability maps</span>
      <span className="text-muted font-tiny font-weight-bold">
        &nbsp;are not&nbsp;
      </span>
      <span className="text-muted font-tiny">
        a recommendation by Propagate Group or Overyield. The Information is
        provided without representation or warranty of any kind. All farms
        should have proper site diligence performed to validate on the ground
        conditions prior to selecting a crop. The accuracy of the underlying
        data will vary by location as we improve data availability & the
        suitability algorithms.
      </span>
    </div>
  );

  return (
    <>
      <IconButton
        size="sm"
        id="SuitabilityDisclaimer"
        className="p-0 text-muted"
        onClick={isMobile ? toggleDisclaimerOpen : undefined}
        color="link"
      >
        Disclaimer
        {isMobile && (
          <ChevronIcon
            className="px-1"
            direction={disclaimerOpen ? 'up' : 'down'}
            width="20px"
          />
        )}
      </IconButton>
      {isMobile ? (
        <Collapse isOpen={disclaimerOpen}>{disclaimerContent}</Collapse>
      ) : (
        <WhiteTooltip
          placement="left"
          target="SuitabilityDisclaimer"
          isOpen={disclaimerOpen}
          toggle={toggleDisclaimerOpen}
          flip={false}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          autohide={false}
        >
          {disclaimerContent}
        </WhiteTooltip>
      )}
    </>
  );
};

const TilesetMenuContainer = styled(PanelMainGroup)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TilesetMenuDropdown = styled(
  ({
    // an array of objects or strings
    options,
    setOption,
    selectedOption,
    // either a string, or a function
    labelKey = 'name',
    placeholder = 'Select...',
    className,
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen((open) => !open);

    return (
      <Dropdown
        isOpen={dropdownOpen}
        toggle={toggle}
        className="d-flex overflow-hidden"
      >
        <IconButton toggle size="sm" className={className}>
          <EllipsisText>
            {(typeof selectedOption === 'string'
              ? selectedOption
              : typeof labelKey === 'function'
              ? labelKey(selectedOption)
              : selectedOption?.[labelKey]) || placeholder}
          </EllipsisText>
        </IconButton>
        {/* The container and flip props are needed to correctly position the open menu
          within the oveflow: scroll table and prevent clipping. */}
        <StyledMenu
          container="body"
          flip={false}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          $fontSmall
        >
          {(options || []).map((option: Tileset | string) => {
            const label =
              typeof option === 'string'
                ? option
                : typeof labelKey === 'function'
                ? labelKey(option)
                : option[labelKey as keyof Tileset];
            return (
              <DropdownItem
                key={typeof option === 'string' ? label : option.id}
                onClick={() => setOption(option)}
              >
                <span>{label}</span>
              </DropdownItem>
            );
          })}
        </StyledMenu>
      </Dropdown>
    );
  },
)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TilesetInfoSection = ({ tileset }: { tileset: Tileset }) => {
  const {
    // @ts-expect-error MapContext not migrated to TS
    mapState: { zoom },
  } = useContext(MapContext);
  const { hoveredColor } = useTilesetContext();

  const { continuousColorToHighlight, discreteColorToHighlight } = useMemo(
    () => getHighlightColors({ tileset, hoveredColor }),
    [tileset, hoveredColor],
  );

  const lulcTileset = tileset.name.startsWith('lulc');
  // This is an arbitrary threshold, chosen to display warning at farm
  // default zoom level
  const maxZoom = tileset.zoomLevels
    ? Math.max(...tileset.zoomLevels)
    : TILESET_MAX_ZOOM;
  const overZoomed = zoom > maxZoom + 2;

  return (
    <>
      <PanelSection key="suitability-menu-key">
        <div className="d-flex">
          <h4>Key</h4>
          {lulcTileset && overZoomed && (
            <>
              <div id="zoom-warning">
                <WarningIcon className="ml-2 mb-2 text-warning" />
              </div>
              <UncontrolledWhiteTooltip
                innerClassName="font-small"
                modifiers={{
                  preventOverflow: { boundariesElement: 'viewport' },
                }}
                placement="top"
                target="zoom-warning"
              >
                {LULC_ZOOM_WARNING}
              </UncontrolledWhiteTooltip>
            </>
          )}
        </div>
        {tileset.continuousKey && (
          <ContinuousKey
            keys={tileset.continuousKey}
            colorToHighlight={continuousColorToHighlight}
          />
        )}
        {tileset.discreteKey && (
          <DiscreteKey
            keys={tileset.discreteKey}
            colorToHighlight={discreteColorToHighlight}
          />
        )}
      </PanelSection>
      {tileset.description.length && (
        <PanelSection key="suitability-menu-info">
          <h4>More Information</h4>
          <div className="py-2">{tileset.description}</div>
        </PanelSection>
      )}
    </>
  );
};
const CropMenuSections = ({ tilesets }: { tilesets: Tileset[] }) => {
  const {
    tileset,
    setTileset,
    tilesetShown,
    setTilesetShown,
  } = useTilesetContext();
  const isMobile = useIsMobile();

  // group tileset options by crop name
  // one crop may have multiple associated tilesets
  const cropOptionsMap = useMemo(
    () => _.groupBy(tilesets, (t) => t.displayNames[0]),
    [tilesets],
  );

  const [selectedCrop, selectedTimeframe] = useMemo(
    () =>
      // if a tileset is already set
      // fill in the dropdown selections from it
      tileset != null && tilesets.includes(tileset)
        ? [tileset.displayNames[0], tileset]
        : [null, null],
    [tileset, tilesets],
  );

  return (
    <div>
      <PanelSection key="dropdowns">
        {!isMobile && <h4>Crop Suitability</h4>}
        <TilesetMenuDropdown
          className="my-1"
          options={[
            ...(tileset ? ['None'] : []),
            ...Object.keys(cropOptionsMap),
          ]}
          setOption={(op: string) => {
            if (op === 'None') {
              setTileset(null);
              return;
            }
            // If a crop is being selected
            // try to default to present day for the timeframe dropdown if it exists
            const presentDayOption = cropOptionsMap[op].find(
              (t: Tileset) => t.displayNames[1] === 'Present Day',
            );
            setTileset(presentDayOption);
            if (!tilesetShown) {
              setTilesetShown(true);
            }
          }}
          selectedOption={selectedCrop}
          placeholder="Select Crop"
        />
        {selectedCrop != null && (
          <TilesetMenuDropdown
            className="my-1"
            options={cropOptionsMap[selectedCrop]}
            setOption={(op: Tileset) => {
              setTileset(op);
              if (!tilesetShown) {
                setTilesetShown(true);
              }
            }}
            selectedOption={selectedTimeframe}
            labelKey={(item: Tileset) => item?.displayNames[1]}
          />
        )}
      </PanelSection>
      {selectedTimeframe != null && (
        <TilesetInfoSection tileset={selectedTimeframe} />
      )}
    </div>
  );
};

export const AttributeTilesetMenu = ({
  tilesets,
  title,
}: {
  tilesets: Tileset[];
  title?: string;
}) => {
  const {
    tileset,
    setTileset,
    tilesetShown,
    setTilesetShown,
  } = useTilesetContext();
  const isMobile = useIsMobile();

  const selectedOption = useMemo(
    () => (tileset != null && tilesets.includes(tileset) ? tileset : null),
    [tilesets, tileset],
  );

  return (
    <>
      <PanelSection key="dropdowns">
        {!isMobile && title && <h4>{title}</h4>}
        <TilesetMenuDropdown
          className="my-1"
          options={[...(tileset ? ['None'] : []), ...tilesets]}
          setOption={(op: Tileset | 'None') => {
            if (op === 'None') {
              setTileset(null);
              return;
            }
            setTileset(op);
            if (!tilesetShown) {
              setTilesetShown(true);
            }
          }}
          selectedOption={selectedOption}
          labelKey={(item: Nullable<Tileset>) => item?.displayNames[0]}
          placeholder="Select Attribute"
        />
      </PanelSection>
      {selectedOption != null && (
        <TilesetInfoSection tileset={selectedOption} />
      )}
    </>
  );
};

const CropTilesetMenu = ({ tilesets }: { tilesets: Tileset[] }) => {
  return (
    <TilesetMenuContainer>
      <CropMenuSections tilesets={tilesets} />
      <PanelSection key="tileset-menu-info">
        <Disclaimer />
      </PanelSection>
    </TilesetMenuContainer>
  );
};

interface TilesetMenuProps {
  filterTilesets: (t: Tileset) => boolean;
  title?: string;
  type: string;
}

const TilesetMenu = ({
  filterTilesets = stubTrue,
  title,
  type,
}: TilesetMenuProps) => {
  const { msg, data } = useQueryWithMsg(TilesetsQuery);
  // Filter tilesets with given function and sort by display name
  const tilesets = useMemo(() => {
    const filteredTilesets = (data?.tilesets || []).filter(filterTilesets);
    filteredTilesets.sort((a: Tileset, b: Tileset) =>
      a.displayNames[0].localeCompare(b.displayNames[0]),
    );
    return filteredTilesets;
  }, [data, filterTilesets]);

  if (msg) return <Message>{msg}</Message>;

  return type === TILESET_TYPE_CROP ? (
    <CropTilesetMenu tilesets={tilesets} />
  ) : (
    <AttributeTilesetMenu tilesets={tilesets} title={title} />
  );
};
export default TilesetMenu;
